import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, size } from 'lodash'
import { defaultTableConfigs } from 'pages/Administration/components/Table/configs'
import { documentTypeColumn } from './config'

import DocumentFieldsPanel from 'pages/Administration/Tabs/Instances/DocumentFieldsPanel'
import Table from 'pages/Administration/components/Table'
import {
  getRowNodeId,
  handleColumnDefs,
  onExportData,
  excelStyles,
  sendNotification,
  sizeTableToFit
} from 'pages/Administration/helpers'
import {
  defaultActions,
  frameworkComponents
} from 'pages/Administration/constants'

import {
  handleOpenModal,
  getPermissionPayload,
  updatePermissionModal,
  resetPermissionModal
} from 'pages/Administration/Tabs/Instances/helpers'
import { createDocumentTypeApi } from 'services/api/administration/documentType'
import { useAgGridTranslation } from 'utils/hooks/useDocumentTranslation'

const DocumentTypesPanel = ({ admin, instanceType, workingTableSelected }) => {
  const { documentType } = admin
  const { t, i18n } = useTranslation(['Administration', 'Common'])

  const [gridApi, setGridApi] = useState(null)
  const [columnApi, setColumnApi] = useState(null)
  const [selectedRows, setSelectedRows] = useState([])

  const [updateSelected, setUpdateSelected] = useState({})
  const [cellHasBeenClicked, setCellHasBeenClicked] = useState(false)

  const { getDocumentTypeByWT, updateDTPermissions, updateDTPermissionsBulk } =
    useMemo(() => createDocumentTypeApi(instanceType), [instanceType])

  useEffect(async () => {
    setSelectedRows([])
    await getDocumentTypeByWT(workingTableSelected)
  }, [workingTableSelected])

  const onRowSelect = useCallback(
    (rowList) => {
      setSelectedRows(rowList)
    },
    [setSelectedRows]
  )

  const notifySuccess = (info) => sendNotification({ t, info, kind: 'success' })

  // UPDATE MODAL

  const onUpdateModalSubmit = async () => {
    const payload = getUpdatePayload(updateSelected)

    try {
      await updateDTPermissionsBulk(payload)
      await getDocumentTypeByWT(workingTableSelected)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('UpdateAll')
    } catch (err) {
      console.error(err)
    }
  }

  const onUpdateSecondaryModalSubmit = async () => {
    const payload = getUpdatePayload(updateSelected)

    try {
      await updateDTPermissions(payload)
      await getDocumentTypeByWT(workingTableSelected)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('Update')
    } catch (err) {
      console.error(err)
    }
  }

  // RESET MODAL

  const onResetModalSubmit = async () => {
    const app = selectedRows[0]
    const payload = getResetPayload(app)

    try {
      await updateDTPermissionsBulk(payload)
      await getDocumentTypeByWT(workingTableSelected)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('ResetAll')
    } catch (err) {
      console.error(err)
    }
  }

  const onResetSecondaryModalSubmit = async () => {
    const app = selectedRows[0]
    const payload = getResetPayload(app)

    try {
      await updateDTPermissions(payload)
      await getDocumentTypeByWT(workingTableSelected)
      gridApi.deselectAll()
      setSelectedRows([])
      notifySuccess('Reset')
    } catch (err) {
      console.error(err)
    }
  }

  const handleResetAllPermissions = () => {
    resetPermissionModal(onResetModalSubmit, onResetSecondaryModalSubmit, t)
  }

  function getUpdatePayload(element) {
    const permissionPayload = getPermissionPayload(element)

    const payload = {
      roleName: element.roleName,
      wtTitle: element.wtTitle,
      activityId: element.idWT,
      documentType: element.documentType,
      permissions: [
        { permission: permissionPayload, roleName: element.roleName }
      ]
    }
    return payload
  }

  function getResetPayload(element) {
    const permissionPayload = {
      read: false,
      import_: false,
      export: false,
      create: false,
      update: false,
      delete: false
    }

    const payload = {
      activityId: element.workingTableId,
      documentType: element.title,
      permissions: [
        { permission: permissionPayload, roleName: element.roleName }
      ]
    }
    return payload
  }

  const headerActions = useMemo(() => {
    if (!isEmpty(gridApi)) {
      return {
        selected: [
          {
            id: 'reset',
            title: t('Common:ResetPermissions'),
            handleClick: () => handleResetAllPermissions()
          }
        ],
        empty: [
          {
            id: 'exportRole',
            title: t('Administration:RolesManagement.Actions.ExportAll'),
            handleClick: () => {
              gridApi &&
                columnApi &&
                onExportData(
                  gridApi,
                  columnApi,
                  t('RolesManagement.DocumentTypeTitle')
                )
            }
          }
        ]
      }
    }
    return defaultActions
  }, [t, gridApi, handleResetAllPermissions, onExportData])

  const { t: agGridT, destroyed } = useAgGridTranslation()

  useEffect(() => {
    if (cellHasBeenClicked) {
      updatePermissionModal(
        onUpdateModalSubmit,
        onUpdateSecondaryModalSubmit,
        t
      )
      setCellHasBeenClicked(false)
    }
  }, [cellHasBeenClicked])

  return (
    <div>
      {/*<h2 className='no-action-view'>*/}
      {/*  {t('RolesManagement.DocumentTypeTitle')}*/}
      {/*</h2>*/}
      <Table
        headerActions={headerActions}
        columnDefs={handleColumnDefs(documentTypeColumn, t)}
        rowData={size(documentType) ? documentType : []}
        getRowNodeId={getRowNodeId}
        excelStyles={excelStyles}
        key={i18n.language}
        localeTextFunc={agGridT}
        onCellClicked={(e) => {
          setUpdateSelected({
            instanceTitle: e.data.instanceTitle,
            instanceId: e.data.instanceId,
            idWT: e.data.workingTableId,
            roleName: e.data.roleName,
            wtTitle: e.data.workingTableTitle,
            column: e.colDef.colId,
            oldPermission: e.data.permission,
            documentType: e.data.title
          })
          if (handleOpenModal(e)) {
            setCellHasBeenClicked(true)
          }
        }}
        onGridReady={({ api, columnApi }) => {
          setGridApi(api)
          setColumnApi(columnApi)
          sizeTableToFit(api)
        }}
        onSelectionChanged={onRowSelect}
        frameworkComponents={frameworkComponents}
        defaultTableConfigs={defaultTableConfigs}
      />
      {selectedRows.length === 1 && (
        <div className='role-view' key={selectedRows[0]}>
          <div className='children-view no-margin'>
            <DocumentFieldsPanel
              admin={admin}
              instanceType={instanceType}
              documentTypeSelected={selectedRows[0]}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default React.memo(DocumentTypesPanel)
